import { format } from 'date-fns'
import { BoatData } from '../types'

/**
 * The playback CSV, built from frames — ONE function for every path that produces the file, so the same rows give the
 * same bytes whether they came from the JSON endpoint or were extracted from the binary history for the requested range.
 * (Moved unchanged out of PlaybackControls.exportToCSV; the Nida docs/105 parity test compares the two paths.)
 *
 * Phone columns as before; the PPK layer rides alongside as ppk_lat / ppk_lon / ppk_dt_s from the layer's nearest point
 * in time (within 5 s, else blank) — one file, both truths, the shape a spreadsheet diff of "what did correction buy us" wants.
 */
export function buildBoatCsv(frames: BoatData[], ppkTrack: BoatData[]): string {
    // Gather all unique keys from parsed data for headers
    const allKeys = new Set<string>()
    frames.forEach(frame => {
        if (frame.parsed) {
            Object.keys(frame.parsed).forEach(k => allKeys.add(k))
        }
    })

    const dynamicHeaders = Array.from(allKeys).sort()
    const ppkAt = (ts: number) => {
        if (!ppkTrack.length) return null
        let lo = 0, hi = ppkTrack.length - 1
        while (lo < hi) { const mid = (lo + hi) >> 1; if (ppkTrack[mid].timestamp < ts) lo = mid + 1; else hi = mid }
        const cand = [ppkTrack[lo], ppkTrack[lo - 1]].filter(Boolean)
            .sort((a, b) => Math.abs(a.timestamp - ts) - Math.abs(b.timestamp - ts))[0]
        return cand && Math.abs(cand.timestamp - ts) <= 5000 ? cand : null
    }
    const ppkHeaders = ppkTrack.length ? ['ppk_lat', 'ppk_lon', 'ppk_dt_s'] : []
    const csvHeaders = ['Timestamp', 'Date', 'Time', 'Boat ID', ...ppkHeaders, ...dynamicHeaders]

    const rows = frames.map(frame => {
        const date = new Date(frame.timestamp)
        const dateStr = format(date, 'yyyy-MM-dd')
        const timeStr = format(date, 'HH:mm:ss')

        const pk = ppkTrack.length ? ppkAt(frame.timestamp) : null
        const ppkCols = ppkTrack.length
            ? [(pk?.parsed as any)?.lat ?? '', (pk?.parsed as any)?.lon ?? '',
               pk ? ((pk.timestamp - frame.timestamp) / 1000).toFixed(1) : '']
            : []
        const row = [
            frame.timestamp,
            dateStr,
            timeStr,
            frame.boatId,
            ...ppkCols,
            ...dynamicHeaders.map(key => {
                const val = frame.parsed ? (frame.parsed as any)[key] : ''
                // Escape commas in values
                return typeof val === 'string' && val.includes(',') ? `"${val}"` : (val ?? '')
            })
        ]
        return row.join(',')
    })

    return [csvHeaders.join(','), ...rows].join('\n')
}
